import { useEffect, useState } from "react";
import { X, ArrowRight, Home, DoorOpen, Layers, TreePine, Wrench, Receipt, ShoppingBag, Lock, Ruler } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

import RoomsTab from "@/components/RoomsTab";
import ExtensionsTab from "@/components/ExtensionsTab";
import ExteriorTab from "@/components/ExteriorTab";
import SystemsTab from "@/components/SystemsTab";
import SoftCostsTab from "@/components/SoftCostsTab";

import { CurrencyProvider, useFormatCurrency } from "@/lib/CurrencyContext";
import { trackEvent } from "@/lib/api";
import {
  estimatedRenovationCost, totalProjectCost, margin as projectMargin, formatPct,
} from "@/lib/calculations";
import { CURRENCIES } from "@/lib/CurrencyContext";

// Pre-seeded "Cape Town flip" — meaningful numbers on first paint.
function buildFlipCostSeed() {
  return {
    id: "demo",
    product: "flipcost",
    name: "Cape Town Flip",
    address: "12 Main Road, Sea Point",
    project_goal: "flip",
    purchase_price: 1850000,
    target_sale_price: 2750000,
    floor_area_sqm: 95,
    property_type: "house",
    renovation_type: "medium",
    currency: "ZAR",
    construction_type: "brick",
    roof_type: "flat",
    foundation_type: "slab",
    wall_finish: "plaster",
    rooms: [],
    extensions: [],
    exterior: [],
    systems: [],
    soft_costs: [],
    photos: [],
    actuals: [],
    notes: "",
    status: "draft",
  };
}

// SpaceCost demo seed — single-space scope, no purchase/sale price.
function buildSpaceCostSeed() {
  return {
    id: "demo",
    product: "spacecost",
    name: "Kitchen Refit · Sea Point",
    address: "Cape Town",
    project_goal: "live",
    purchase_price: 0,
    target_sale_price: 0,
    floor_area_sqm: 12,
    property_type: "kitchen",
    renovation_type: "medium",
    currency: "ZAR",
    construction_type: "",
    roof_type: "",
    foundation_type: "",
    wall_finish: "",
    rooms: [],
    extensions: [],
    exterior: [],
    systems: [],
    soft_costs: [],
    photos: [],
    actuals: [],
    extra_room_packs: 0,
    notes: "",
    status: "draft",
  };
}

const FLIPCOST_TABS = [
  { id: "overview", label: "Overview", icon: Home },
  { id: "rooms", label: "Rooms", icon: DoorOpen },
  { id: "extensions", label: "Extensions", icon: Layers },
  { id: "exterior", label: "Exterior", icon: TreePine },
  { id: "systems", label: "Systems", icon: Wrench },
  { id: "soft", label: "Soft Costs", icon: Receipt },
];

// SpaceCost: drop Exterior + Systems (whole-property concepts).
const SPACECOST_TABS = [
  { id: "overview", label: "Overview", icon: Ruler },
  { id: "rooms", label: "Rooms", icon: DoorOpen },
  { id: "extensions", label: "Extensions", icon: Layers },
  { id: "soft", label: "Soft Costs", icon: Receipt },
];

const PROPERTY_TYPES = [
  { value: "house", label: "House" },
  { value: "apartment", label: "Apartment" },
  { value: "townhouse", label: "Townhouse" },
  { value: "duplex", label: "Duplex" },
];
const SPACE_TYPES = [
  { value: "kitchen", label: "Kitchen" },
  { value: "bathroom", label: "Bathroom" },
  { value: "bedroom", label: "Bedroom" },
  { value: "office", label: "Home office" },
  { value: "garden", label: "Garden / outdoor" },
];
const RENOVATION_TYPES = [
  { value: "light", label: "Light" },
  { value: "medium", label: "Medium" },
  { value: "heavy", label: "Heavy" },
  { value: "full", label: "Full Refurb" },
];

/**
 * Live, no-signup demo. `product` switches between FlipCost (whole-property,
 * profit math, exterior/systems tabs) and SpaceCost (single-space, mint
 * theme, no valuation). Same shared tab components — filtered by product
 * so the demo mirrors the post-purchase experience exactly.
 */

// Tab-level walkthrough copy — answers "what am I looking at?" inline so
// users don't bounce after the first tab. Each entry is intentionally short
// (one line) — full guides live on /how-it-works.
const DEMO_TAB_HINTS = {
  flipcost: {
    overview: "Edit the deal: purchase price, target sale, size. Watch the metrics on the right update live.",
    rooms: "Add a room → the tool auto-generates work items at finish-level rates. Tweak any line to match your contractor quote.",
    extensions: "Adding square metres? Plan them here — costs are calculated from build type + size.",
    exterior: "Roof, paint, garden, driveway. Toggle what's in scope and adjust rates.",
    systems: "Wiring, plumbing, HVAC. Whole-property upgrades that aren't tied to a single room.",
    soft: "Architect fees, permits, contingency. Auto-scale with floor area & rooms.",
  },
  spacecost: {
    overview: "Tell us the space: type, size, condition, finish level. The tool seeds a realistic line-item budget.",
    rooms: "Each room is a structured plan. Click into a room to fine-tune any line item.",
    extensions: "Optional: extending the space? Add it here with a build-type rate.",
    soft: "Architect / contractor / permit fees, auto-scaled to room size.",
  },
};

function DemoTabHint({ product, tab }) {
  const map = DEMO_TAB_HINTS[product] || DEMO_TAB_HINTS.flipcost;
  const text = map[tab];
  if (!text) return null;
  return (
    <div
      className="mb-4 flex items-start gap-2.5 rounded-lg border border-sage-200 bg-sage-50 text-sage-800 px-3.5 py-2.5"
      data-testid={`demo-tab-hint-${tab}`}
    >
      <span className="shrink-0 mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-sage-200 text-sage-700 text-[10px] font-bold">i</span>
      <p className="text-[12.5px] leading-snug">
        {text}
      </p>
    </div>
  );
}

export default function DemoDialog({ open, onOpenChange, source, product = "flipcost" }) {
  const isSpaceCost = product === "spacecost";
  const buildSeed = isSpaceCost ? buildSpaceCostSeed : buildFlipCostSeed;
  const tabs = isSpaceCost ? SPACECOST_TABS : FLIPCOST_TABS;
  const [project, setProject] = useState(buildSeed);
  const [activeTab, setActiveTab] = useState("overview");
  const navigate = useNavigate();

  // Re-seed when the product changes mid-session (chooser → flipcost → close → spacecost).
  useEffect(() => {
    if (open) {
      setProject(buildSeed());
      setActiveTab("overview");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product]);

  // Ping analytics whenever the demo opens. Includes product so we can split
  // FlipCost vs SpaceCost demo conversion.
  useEffect(() => {
    if (open) {
      const pageSlug = (typeof window !== "undefined" && window.location?.pathname) || "";
      trackEvent("demo_open", source || `path:${pageSlug}`, { tab: activeTab, product });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onChange = (patch) => setProject((p) => ({ ...p, ...patch }));

  const goPay = () => {
    onOpenChange(false);
    navigate(isSpaceCost ? "/spacecost/pricing" : "/pricing");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="demo-dialog"
        data-product={product}
        data-theme={isSpaceCost ? "spacecost" : undefined}
        className="bg-[#FAFAF9] border-black/[0.08] text-[#27272A] sm:max-w-[1100px] w-[95vw] max-h-[92vh] overflow-hidden p-0 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 sm:px-6 pt-5 pb-3 border-b border-black/[0.06] bg-white">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-sage-700 bg-sage-100 border border-sage-200 rounded-full px-2.5 py-0.5">
              Live demo · no signup
            </div>
            <div className="fc-heading text-[#1C1C1C] text-lg sm:text-xl mt-2 truncate">
              Try {isSpaceCost ? "SpaceCost" : "FlipCost"} — {project.name}
            </div>
            <div className="text-[12px] text-[#78716C] mt-0.5">
              {isSpaceCost
                ? "Edit the example, add a line item, see the room total move. Saving requires a paid project."
                : "Edit the example, add a room or two, see the numbers move. Saving requires a paid project."}
            </div>
          </div>
          <button
            data-testid="demo-close-btn"
            onClick={() => onOpenChange(false)}
            className="text-[#78716C] hover:text-[#1C1C1C] -mr-1 mt-1 shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-3 sm:px-4 py-2 bg-white border-b border-black/[0.06] overflow-x-auto fc-no-scrollbar">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`demo-tab-${t.id}`}
                onClick={() => setActiveTab(t.id)}
                className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors ${
                  active
                    ? "bg-sage-100 text-sage-700 border border-sage-200"
                    : "text-[#57534E] hover:bg-sage-50/60"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <CurrencyProvider currency={project.currency}>
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">
            {/* Inline per-tab explanation — replaces having to read the
                /how-it-works page first. Updates as the user switches tabs. */}
            <DemoTabHint product={product} tab={activeTab} />
            {activeTab === "overview" && (
              <DemoOverview project={project} onChange={onChange} isSpaceCost={isSpaceCost} />
            )}
            {activeTab === "rooms" && (
              <RoomsTab project={project} onRoomsChange={(rooms) => onChange({ rooms })} />
            )}
            {activeTab === "extensions" && (
              <ExtensionsTab project={project} onChange={onChange} />
            )}
            {activeTab === "exterior" && !isSpaceCost && (
              <ExteriorTab project={project} onChange={onChange} />
            )}
            {activeTab === "systems" && !isSpaceCost && (
              <SystemsTab project={project} onChange={onChange} />
            )}
            {activeTab === "soft" && (
              <SoftCostsTab project={project} onChange={onChange} />
            )}
          </div>
        </CurrencyProvider>

        {/* Footer CTA */}
        <div className="border-t border-black/[0.06] bg-white px-5 sm:px-6 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-[12px] text-[#78716C]">
            <Lock className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">
              {isSpaceCost
                ? "Demo only — changes aren't saved. Buy a SpaceCost project to save your own."
                : "Demo only — changes aren't saved. Buy a Single Project unlock to save your own."}
            </span>
            <span className="sm:hidden">Demo only — buy to save.</span>
          </div>
          <Button
            data-testid="demo-pay-cta"
            onClick={goPay}
            className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-9 px-4 text-[13px] font-medium inline-flex items-center gap-1.5"
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            {isSpaceCost ? "Buy a SpaceCost project (R180)" : "Buy a project (R280)"}
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Lightweight Overview tailored for the demo: editable Key Inputs only.
// SpaceCost variant drops the purchase/sale price and profit math.
function DemoOverview({ project, onChange, isSpaceCost }) {
  const fmt = useFormatCurrency();
  const reno = estimatedRenovationCost(project);
  const total = totalProjectCost(project);
  const profit = (Number(project.target_sale_price) || 0) - total;
  const margin = projectMargin(project);

  return (
    <div className="space-y-5" data-testid="demo-overview">
      {/* Editable basics */}
      <div className="fc-card p-5">
        <div className="fc-label text-sage-700">Project basics</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
          <Field label={isSpaceCost ? "Project name" : "Project name"}>
            <Input
              data-testid="demo-name-input"
              value={project.name}
              onChange={(e) => onChange({ name: e.target.value })}
            />
          </Field>
          <Field label={isSpaceCost ? "Location (optional)" : "Address"}>
            <Input
              data-testid="demo-address-input"
              value={project.address}
              onChange={(e) => onChange({ address: e.target.value })}
            />
          </Field>
          <Field label="Currency">
            <Select value={project.currency} onValueChange={(v) => onChange({ currency: v })}>
              <SelectTrigger data-testid="demo-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={isSpaceCost ? "Space type" : "Property type"}>
            <Select value={project.property_type} onValueChange={(v) => onChange({ property_type: v })}>
              <SelectTrigger data-testid="demo-property-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(isSpaceCost ? SPACE_TYPES : PROPERTY_TYPES).map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </div>

      {/* Key inputs — FlipCost shows purchase/sale prices; SpaceCost just the space size. */}
      <div className="fc-card p-5">
        <div className="fc-label text-sage-700">Key inputs</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
          {!isSpaceCost && (
            <>
              <Field label={`Purchase price (${project.currency})`}>
                <Input
                  data-testid="demo-purchase-input"
                  type="number"
                  value={project.purchase_price}
                  onChange={(e) => onChange({ purchase_price: Number(e.target.value) || 0 })}
                />
              </Field>
              <Field label={`Target sale price (${project.currency})`}>
                <Input
                  data-testid="demo-sale-input"
                  type="number"
                  value={project.target_sale_price}
                  onChange={(e) => onChange({ target_sale_price: Number(e.target.value) || 0 })}
                />
              </Field>
            </>
          )}
          <Field label={isSpaceCost ? "Space size (sqm)" : "Floor area (sqm)"}>
            <Input
              data-testid="demo-area-input"
              type="number"
              value={project.floor_area_sqm}
              onChange={(e) => onChange({ floor_area_sqm: Number(e.target.value) || 0 })}
            />
          </Field>
          {!isSpaceCost && (
            <Field label="Renovation scope">
              <Select value={project.renovation_type} onValueChange={(v) => onChange({ renovation_type: v })}>
                <SelectTrigger data-testid="demo-reno-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RENOVATION_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
          )}
        </div>
      </div>

      {/* Live summary — SpaceCost shows budget only; FlipCost shows full profit math. */}
      <div className={`grid grid-cols-2 ${isSpaceCost ? "lg:grid-cols-2" : "lg:grid-cols-4"} gap-4`} data-testid="demo-summary">
        <Stat label="Renovation" value={fmt(reno)} />
        <Stat label="Total cost" value={fmt(total)} />
        {!isSpaceCost && (
          <>
            <Stat
              label="Profit"
              value={fmt(profit)}
              valueClass={profit >= 0 ? "text-sage-700" : "text-red-600"}
            />
            <Stat
              label="Margin"
              value={formatPct(margin)}
              valueClass={margin >= 0 ? "text-sage-700" : "text-red-600"}
            />
          </>
        )}
      </div>

      <div className="text-[12px] text-[#78716C]">
        <strong>Try it:</strong> jump to the <em>Rooms</em> tab and add a {isSpaceCost ? "kitchen" : "Kitchen"} —
        watch the renovation total {isSpaceCost ? "update instantly" : "and profit refresh instantly"}.
        {isSpaceCost
          ? " Then try Soft Costs to layer in design fees and contingency."
          : " Then try Exterior or Systems to layer in more cost."}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.15em] text-[#A8A29E] mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function Stat({ label, value, valueClass = "text-[#1C1C1C]" }) {
  return (
    <div className="fc-card p-4">
      <div className="fc-label">{label}</div>
      <div className={`fc-heading text-xl mt-1 ${valueClass}`}>{value}</div>
    </div>
  );
}
